import { TooltipProvider } from "@/components/ui/tooltip";
import { DEFAULT_TEXT_LABEL_FONT_SIZE } from "@/pages/canvas/types/canvasNodeData";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarDivider,
  CanvasToolbarFontSizeMenu,
  CanvasToolbarTypefaceMenu,
} from "../components/toolbar";
import {
  CanvasToolbarAlignMenu,
  CanvasToolbarFormatMenu,
} from "../components/toolbar/CanvasToolbarFormatMenu";
import {
  readCanvasAlignment,
  toggleCanvasStrikethrough,
} from "../components/toolbar/canvasFormatCommands";
import { applyCanvasListType } from "../components/toolbar/canvasListCommands";
import { NOTE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { CanvasEmojiPicker } from "./shared/CanvasEmojiPicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

// Notes keep their own family values (Simple is the system face here).
const NOTE_TYPEFACES = [
  { name: "Simple", value: "system-ui, -apple-system, sans-serif" },
  { name: "Bookish", value: "Georgia, serif" },
  { name: "Technical", value: "'Courier New', monospace" },
  { name: "Scribbled", value: "'Comic Sans MS', cursive" },
  { name: "Modern", value: "'Inter', system-ui, sans-serif" },
];

interface TextNoteFloatingToolbarProps {
  visible: boolean;
  noteColor: string;
  fontFamily?: string;
  fontSize?: string;
  onColorChange: (color: string) => void;
  onFontFamilyChange: (fontFamily: string) => void;
  onFontSizeChange: (fontSize: string) => void;
  editor: LexicalEditor | null;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}

export function TextNoteFloatingToolbar({
  visible,
  noteColor,
  fontFamily = "system-ui, -apple-system, sans-serif",
  fontSize = DEFAULT_TEXT_LABEL_FONT_SIZE,
  onColorChange,
  onFontFamilyChange,
  onFontSizeChange,
  editor,
  onPopoverOpenChange,
}: TextNoteFloatingToolbarProps) {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlignment, setTextAlignment] = useState<
    "left" | "center" | "right"
  >("left");

  // Refs updated synchronously before notify so parent latch never sees stale OR.
  const isColorOpenRef = useRef(false);
  const isFontFamilyOpenRef = useRef(false);
  const isFontSizeOpenRef = useRef(false);
  const isFormatOpenRef = useRef(false);
  const isAlignOpenRef = useRef(false);
  const isEmojiOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isColorOpenRef.current ||
        isFontFamilyOpenRef.current ||
        isFontSizeOpenRef.current ||
        isFormatOpenRef.current ||
        isAlignOpenRef.current ||
        isEmojiOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontFamilyOpen = useCallback(
    (open: boolean) => {
      isFontFamilyOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontSizeOpen = useCallback(
    (open: boolean) => {
      isFontSizeOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFormatOpen = useCallback(
    (open: boolean) => {
      isFormatOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleAlignOpen = useCallback(
    (open: boolean) => {
      isAlignOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleEmojiOpen = useCallback(
    (open: boolean) => {
      isEmojiOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  // The align indicator comes from the content on mount: a re-selected note
  // has no caret, so the update listener below never fires for it.
  useEffect(() => {
    setTextAlignment(readCanvasAlignment(editor));
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat("bold"));
          setIsItalic(selection.hasFormat("italic"));
          setIsUnderline(selection.hasFormat("underline"));
          setIsStrikethrough(selection.hasFormat("strikethrough"));
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();
          if ($isElementNode(element)) {
            const formatType = element.getFormatType();
            if (formatType === "center" || formatType === "right") {
              setTextAlignment(formatType);
            } else {
              setTextAlignment("left");
            }
          }
        }
      });
    });
  }, [editor]);

  const handleBoldChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  }, [editor]);

  const handleItalicChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  }, [editor]);

  const handleUnderlineChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
  }, [editor]);

  const handleStrikethroughChange = useCallback(() => {
    toggleCanvasStrikethrough(editor);
  }, [editor]);

  const handleAlignChange = useCallback(
    (align: "left" | "center" | "right") => {
      editor?.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
      setTextAlignment(align);
    },
    [editor],
  );

  if (!visible) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("input")) return;
          e.preventDefault();
        }}
      >
        {/* Color — chat, rename, download, and expand-to-fit have moved
            into the note's chrome (or retired); the bar keeps only what
            the chrome does not own. */}
        <CanvasColorPickerButton
          colors={NOTE_COLORS}
          activeValue={noteColor}
          tooltip="Note color"
          onColorChange={onColorChange}
          onOpenChange={handleColorOpen}
        />

        <div className="w-px h-4 bg-border mx-0.5" />

        <CanvasToolbarTypefaceMenu
          fontFamily={fontFamily}
          fonts={NOTE_TYPEFACES}
          onFontFamilyChange={onFontFamilyChange}
          onOpenChange={handleFontFamilyOpen}
        />

        <CanvasToolbarFontSizeMenu
          fontSize={fontSize}
          onFontSizeChange={onFontSizeChange}
          onOpenChange={handleFontSizeOpen}
        />

        <CanvasToolbarDivider />

        <CanvasToolbarFormatMenu
          isBold={isBold}
          isItalic={isItalic}
          isUnderline={isUnderline}
          isStrikethrough={isStrikethrough}
          onBoldChange={handleBoldChange}
          onItalicChange={handleItalicChange}
          onUnderlineChange={handleUnderlineChange}
          onStrikethroughChange={handleStrikethroughChange}
          onListTypeSelect={(type) => applyCanvasListType(editor, type)}
          onOpenChange={handleFormatOpen}
        />

        <CanvasToolbarAlignMenu
          alignment={textAlignment}
          onAlignChange={handleAlignChange}
          onOpenChange={handleAlignOpen}
        />

        <CanvasEmojiPicker
          editor={editor}
          onPopoverOpenChange={handleEmojiOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
